<template>
  <li v-if="visible" class="extras-mg-nuevo-grupo">
    <button
      type="button"
      class="button button--primary button--regular extras-mg-nuevo-grupo__flecha"
      title="Nuevo grupo de elementos"
      aria-haspopup="dialog"
      :aria-expanded="abierto"
      @click="alternar"
    >
      <i class="iconoir-nav-arrow-down"></i>
    </button>
    <form
      v-if="abierto"
      ref="recuadro"
      class="extras-mg-nuevo-grupo__recuadro"
      @submit.prevent="crear"
      @keydown.esc="cerrar"
    >
      <div class="extras-mg-nuevo-grupo__titulo">Nuevo grupo de elementos</div>
      <input
        ref="nombre"
        v-model="nombre"
        class="input extras-mg-nuevo-grupo__nombre"
        type="text"
        maxlength="255"
        placeholder="Nombre del grupo"
        :disabled="creando"
      />
      <Button
        type="primary"
        size="regular"
        full-width
        :loading="creando"
        :disabled="creando || !nombre.trim()"
      >
        Crear grupo
      </Button>
    </form>
  </li>
</template>

<script>
import Button from '@baserow/modules/core/components/Button.vue'
import { notifyIf } from '@baserow/modules/core/utils/error'

// Los colores de grupo de Monday, en el orden en que los va asignando. Son
// clases de Baserow; la piel las pinta con los valores de Monday.
export const COLORES_DE_GRUPO = [
  'dark-blue',
  'dark-green',
  'dark-purple',
  'dark-orange',
  'dark-red',
  'dark-pink',
  'dark-cyan',
  'dark-yellow',
  'darker-blue',
  'darker-green',
]

/** El primer color de grupo que nadie usa todavía; si ya se usaron todos, vuelve a empezar. */
export function siguienteColor(opciones) {
  const usados = new Set((opciones || []).map((o) => o.color))
  return (
    COLORES_DE_GRUPO.find((c) => !usados.has(c)) ||
    COLORES_DE_GRUPO[(opciones || []).length % COLORES_DE_GRUPO.length]
  )
}

/**
 * "Nuevo grupo de elementos", la flecha que Monday pone junto a "Nuevo
 * elemento".
 *
 * En esta instancia un grupo es una opción del campo de selección por el que
 * se agrupa la vista. Crear un grupo es agregar esa opción —al principio de la
 * lista, para que el grupo salga arriba como en Monday, con el siguiente color
 * de grupo libre— y meterle un primer elemento, porque en Baserow un grupo sin
 * filas no se dibuja.
 *
 * Sólo cuando la cuadrícula está agrupada por una selección única y quien mira
 * puede cambiar ese campo y crear filas (un Lector no ve la flecha).
 */
export default {
  name: 'NuevoGrupo',
  components: { Button },
  props: {
    database: { type: Object, required: false, default: null },
    table: { type: Object, required: true },
    view: { type: Object, required: false, default: null },
    fields: { type: Array, required: false, default: () => [] },
    isPublicView: { type: Boolean, required: false, default: false },
    storePrefix: { type: String, required: false, default: '' },
  },
  data() {
    return { abierto: false, nombre: '', creando: false }
  },
  computed: {
    campoDeGrupo() {
      if (this.view?.type !== 'grid' || this.storePrefix === '') {
        return null
      }
      const activos =
        this.$store.getters[this.storePrefix + 'view/grid/getActiveGroupBys'] ||
        []
      if (!activos.length) {
        return null
      }
      const campo = this.fields.find((f) => f.id === activos[0].field)
      return campo && campo.type === 'single_select' ? campo : null
    },
    visible() {
      if (this.isPublicView || !this.database || !this.campoDeGrupo) {
        return false
      }
      const ws = this.database.workspace.id
      return (
        this.$hasPermission('database.table.field.update', this.campoDeGrupo, ws) &&
        this.$hasPermission('database.table.create_row', this.table, ws)
      )
    },
  },
  beforeUnmount() {
    document.removeEventListener('mousedown', this.fueraDelRecuadro, true)
  },
  methods: {
    alternar() {
      this.abierto ? this.cerrar() : this.abrir()
    },
    abrir() {
      this.abierto = true
      this.nombre = ''
      document.addEventListener('mousedown', this.fueraDelRecuadro, true)
      this.$nextTick(() => this.$refs.nombre?.focus())
    },
    cerrar() {
      this.abierto = false
      document.removeEventListener('mousedown', this.fueraDelRecuadro, true)
    },
    fueraDelRecuadro(event) {
      if (!this.$el.contains(event.target)) {
        this.cerrar()
      }
    },
    async crear() {
      const nombre = this.nombre.trim()
      const campo = this.campoDeGrupo
      if (!nombre || !campo || this.creando) {
        return
      }
      this.creando = true
      try {
        // Si ya hay una opción con ese nombre (p. ej. la que dejó un intento
        // que falló al crear la fila), se usa esa: si no, el reintento la
        // duplicaría y el nombre ya no diría a cuál ir.
        let opcion = campo.select_options.find((o) => o.value === nombre)
        if (!opcion) {
          const antes = new Set(campo.select_options.map((o) => o.id))
          const opciones = [
            { value: nombre, color: siguienteColor(campo.select_options) },
            ...campo.select_options.map((o) => ({
              id: o.id,
              value: o.value,
              color: o.color,
            })),
          ]
          await this.$store.dispatch('field/update', {
            field: campo,
            type: campo.type,
            values: { select_options: opciones },
          })
          const actualizado =
            this.$store.getters['field/getAll'].find((f) => f.id === campo.id) ||
            campo
          opcion = actualizado.select_options.find((o) => !antes.has(o.id))
        }
        const campos = this.$store.getters['field/getAll']
        await this.$store.dispatch(
          this.storePrefix + 'view/grid/createNewRowInGroup',
          {
            view: this.view,
            table: this.table,
            fields: campos,
            // Como las rutas que manda el servidor: el id de la opción. Con la
            // opción entera la fila salía marcada como "se ha movido".
            path: { [`field_${campo.id}`]: opcion.id },
            values: {},
            before: null,
            selectPrimaryCell: false,
          }
        )
        // El store dibuja el grupo nuevo al momento, pero arma su nombre de la
        // fila (optimista) y la banda nacía vacía hasta recargar la página. Se
        // pide la vista otra vez para que la banda traiga el nombre del
        // servidor. Probado en producción: ~1.7 s en un tablero chico.
        await this.$store.dispatch(this.storePrefix + 'view/grid/refresh', {
          view: this.view,
          fields: campos,
        })
        this.cerrar()
      } catch (error) {
        notifyIf(error, 'field')
      } finally {
        this.creando = false
      }
    },
  },
}
</script>
